import type { Block, Building, Room, Section } from "../types/objects.types";

export const initialBuildings: Building[] = [
	{
		id: "dream",
		name: "Dream",
		code: "samo",
		initials: "DR",
		tone: "blue",
		blocksCount: 4,
		soldCount: 85,
		availableCount: 165,
		totalCount: 250,
		address: "Toshkent shahar, Chilonzor tumani, Lutfiy ko'chasi",
		showroomUrl: "https://my.matterport.com/show/?m=dream-3d-demo",
	},
	{
		id: "yunusobod",
		name: "Yunusobod",
		code: "yunus",
		initials: "YU",
		tone: "green",
		blocksCount: 3,
		soldCount: 40,
		availableCount: 98,
		totalCount: 138,
		address: "Toshkent shahar, Yunusobod tumani, 4-kvartal",
		showroomUrl: "https://my.matterport.com/show/?m=yunusobod-3d-demo",
	},
	{
		id: "unicorp-city",
		name: "Unicorp City",
		code: "city",
		initials: "UC",
		tone: "violet",
		blocksCount: 6,
		soldCount: 73,
		availableCount: 201,
		totalCount: 274,
		address: "Toshkent shahar, Yakkasaroy tumani, Shota Rustaveli",
		showroomUrl: "https://my.matterport.com/show/?m=city-3d-demo",
	},
];

export const initialBlocks: Block[] = [
	// Dream
	{
		id: "dream-a",
		buildingId: "dream",
		name: "A Blok",
		floorsCount: 9,
		sectionsCount: 2,
		roomTypesCount: ["2-xonali: 18", "3-xonali: 18"],
		priceRange: "384–596 mln UZS",
		areaRange: "51–79 m²",
		hasInteractivePlan: true,
	},
	{
		id: "dream-b",
		buildingId: "dream",
		name: "B Blok",
		floorsCount: 12,
		sectionsCount: 2,
		roomTypesCount: ["1-xonali: 24", "2-xonali: 24", "3-xonali: 12"],
		priceRange: "310–620 mln UZS",
		areaRange: "42–91 m²",
		hasInteractivePlan: true,
	},
	{
		id: "dream-c",
		buildingId: "dream",
		name: "C Blok",
		floorsCount: 9,
		sectionsCount: 1,
		roomTypesCount: ["2-xonali: 36"],
		priceRange: "440–540 mln UZS",
		areaRange: "67–72 m²",
		hasInteractivePlan: true,
	},
	// Yunusobod
	{
		id: "yunus-a",
		buildingId: "yunusobod",
		name: "A Blok",
		floorsCount: 9,
		sectionsCount: 2,
		roomTypesCount: ["1-xonali: 18", "2-xonali: 18"],
		priceRange: "280–450 UZS",
		areaRange: "38–65 m²",
		hasInteractivePlan: false,
	},
	// Unicorp City
	{
		id: "city-r",
		buildingId: "unicorp-city",
		name: "R Blok",
		floorsCount: 16,
		sectionsCount: 3,
		roomTypesCount: ["2-xonali: 48", "3-xonali: 48", "Penthouse: 6"],
		priceRange: "650–1800 UZS",
		areaRange: "55–180 m²",
		hasInteractivePlan: true,
	},
];

export const initialSections: Section[] = [
	// Dream - A Blok
	{ id: "dream-a-s1", blockId: "dream-a", name: "1-padezd", roomsCount: 36 },
	{ id: "dream-a-s2", blockId: "dream-a", name: "2-padezd", roomsCount: 36 },
	// Dream - B Blok
	{ id: "dream-b-s1", blockId: "dream-b", name: "1-padezd", roomsCount: 30 },
	{ id: "dream-b-s2", blockId: "dream-b", name: "2-padezd", roomsCount: 30 },
	// Dream - C Blok
	{ id: "dream-c-s1", blockId: "dream-c", name: "1-padezd", roomsCount: 36 },
	// Yunusobod - A Blok
	{ id: "yunus-a-s1", blockId: "yunus-a", name: "1-padezd", roomsCount: 36 },
	{ id: "yunus-a-s2", blockId: "yunus-a", name: "2-padezd", roomsCount: 36 },
	// City - R Blok
	{ id: "city-r-s1", blockId: "city-r", name: "1-padezd", roomsCount: 45 },
	{ id: "city-r-s2", blockId: "city-r", name: "2-padezd", roomsCount: 45 },
	{ id: "city-r-s3", blockId: "city-r", name: "3-padezd", roomsCount: 45 },
];

// Helper to generate rooms dynamically
export function generateRooms(): Room[] {
	const rooms: Room[] = [];
	const sts: ("sotuvda" | "sotilgan" | "band" | "bekor")[] = [
		"sotuvda",
		"sotilgan",
		"sotuvda",
		"band",
		"sotuvda",
		"sotilgan",
		"sotuvda",
		"sotuvda",
		"sotilgan",
	];
	const rc = [1, 2, 3, 2, 3, 2, 1, 2];

	// For each section
	for (const section of initialSections) {
		const block = initialBlocks.find((b) => b.id === section.blockId);
		if (!block) {
			continue;
		}

		const floors = block.floorsCount;
		const roomsPerFloor = 8;

		for (let f = floors; f >= 1; f--) {
			for (let r = 1; r <= roomsPerFloor; r++) {
				const stIndex = (f * 3 + r) % sts.length;
				const status = sts[stIndex];
				const roomsCountVal = rc[(r - 1) % rc.length];
				const areaVal = 50 + r * 4;
				const roomNumber = `${f}${String(r).padStart(2, "0")}`;
				const roomId = `${section.id}-${roomNumber}`;

				const basePrice = Math.round(areaVal * 6.5 * 10) / 10; // e.g. in million UZS

				const room: Room = {
					id: roomId,
					sectionId: section.id,
					blockId: block.id,
					buildingId: block.buildingId,
					roomNumber,
					floor: f,
					roomsCount: roomsCountVal,
					area: areaVal,
					terraceArea: r % 4 === 0 ? 10.0 : 0,
					finishType: r % 2 === 0 ? "tamirli" : "tamirsiz",
					basePrice: Math.round(basePrice * 10) / 10,
					finishedPrice: r % 2 === 0 ? Math.round(basePrice * 1.15 * 10) / 10 : 0,
					hasCadastre: r % 3 === 0,
					isHighlighted: f === 5 && r === 4,
					status,
					images: ["plan.png", "3d.png", "terrace.png"],
				};

				// Add buyer details for some sold/pending rooms
				if (status === "sotilgan" || status === "band") {
					room.client = {
						id: r % 2 === 0 ? "mukhlis-pardayeva" : "jasur-mirzayev",
						saleId: r % 2 === 0 ? "sale-41" : "sale-50",
						name: r % 2 === 0 ? "Mukhlis Pardayeva" : "Jasur Mirzayev",
						avatar: r % 2 === 0 ? "MP" : "JM",
						phone: r % 2 === 0 ? "+998 93 331 35 15" : "+998 90 987 65 43",
						manager: "Sardor T.",
						percentPaid: status === "sotilgan" ? 100 : 35,
						nextPaymentDate: "2026-07-01",
						nextPaymentAmount: "11.85 mln UZS",
						debtBalance: status === "sotilgan" ? "0 UZS" : "447 mln UZS",
						status: status === "sotilgan" ? "to'liq" : "muddatli",
					};
					if (status === "band") {
						room.paymentProgress = 35;
					} else {
						room.paymentProgress = 100;
					}
				}

				rooms.push(room);
			}
		}
	}

	return rooms;
}
